import React, { useState } from "react";
import { AcmeCoOperationErrorIcon } from "../../assets/icons";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";
import { useDispatch } from "react-redux";

const PlatformBranding = ({ settings = [] }) => {
  const dispatch = useDispatch();

  // Map API names to toggle IDs
  const nameToIdMap = {
    allow_tenant_branding_customization: "atbc",
    allow_custom_logo_upload: "aclu",
    allow_color_customization: "acsc",
    enable_white_label: "ewl",
    allow_custom_domain: "acd",
  };

  // Reverse map (IDs → API names)
  const idToNameMap = Object.fromEntries(
    Object.entries(nameToIdMap).map(([apiName, id]) => [id, apiName])
  );

  // Initialize state from API
  const initialState = settings.reduce(
    (acc, s) => {
      const id = nameToIdMap[s.name];
      if (id) {
        acc[id] = s.value === true || s.value === "true"; // normalize to boolean
      }
      return acc;
    },
    {
      atbc: false,
      aclu: false,
      acsc: false,
      ewl: false,
      acd: false,
    }
  );

  const [formValues, setFormValues] = useState(initialState);

  const tenantCustomizationToggles = [
    { id: "atbc", label: "Allow tenant branding customization" },
    { id: "aclu", label: "Allow custom logo uploads" },
    { id: "acsc", label: "Allow color scheme customization" },
  ];

  const advancedOptionsToggles = [
    { id: "ewl", label: "Enable white-labeling (Enterprise)" },
    { id: "acd", label: "Allow custom domains (Enterprise)" },
  ];

  const handleToggle = (id) => {
    setFormValues((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSaveChanges = () => {
    // Convert local IDs back to API names
    const payload = Object.entries(formValues).map(([id, value]) => ({
      name: idToNameMap[id], // real API field name
      value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {},
        onFailure: (error) => {
          console.error("Failed to update branding settings:", error);
        },
      })
    );
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Platform Customisation Controls
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Configure what branding and customization options are
                  available to tenants
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tenant Customization */}
        <div className="form-group">
          <label className="!text-black pb-2">Tenant Customization</label>
          <div className="grid gap-4">
            {tenantCustomizationToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Advanced Options */}
        <div className="form-group">
          <label className="!text-black pb-2">Advanced Options</label>
          <div className="grid gap-4">
            {advancedOptionsToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Branding Guidelines Info */}
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Branding Guidelines
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Maintain brand consistency while allowing appropriate tenant
                  customization. White-labeling and custom domains are typically
                  reserved for enterprise customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-auto flex gap-3 pt-6 justify-end flex-wrap">
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save Branding Rules
        </button>
      </div>
    </>
  );
};

export default PlatformBranding;
